import React, { useEffect, useState } from "react";

import { lighten, Theme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Box
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import { MoreVertRounded } from "@mui/icons-material";
import {
  gray1,
  lighestGray,
  lightGray,
  pprBorderColorA,
  strongGray,
  textColor,
  textGrayH1,
  toolbarBG1,
  white
} from "../DashStyleGuides";
import { GetTopOverdueDespatches } from "../ApiServices/serviceCalls";
import { DispatchProduct } from "../../../models/dispatch.product";
import { formatInTimeZone } from "../../../util/formatInTimezone";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      borderRadius: 12,
      height: 300
    },
    paper: {
      width: "100%",
      marginBottom: theme.spacing(2),
      border: `1px solid ${pprBorderColorA}`,
      borderRadius: 12,
      "& .MuiTableContainer-root": {
        height: 250
      }
    },
    table: {
      minWidth: 392,
      "& .tblrow": {
        height: 44,
        fontSize: 12,
        fontFamily: "Poppins",
        fontWeight: 400,
        color: textColor
      },
      "& .light": {
        background: white
      },
      "& .darker": { background: lightGray }
    },
    visuallyHidden: {
      border: 0,
      clip: "rect(0 0 0 0)",
      height: 1,
      margin: -1,
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      top: 20,
      width: 1
    },
    tblPage: {
      maxWidth: 392,
      "& .MuiToolbar-gutters": { padding: 0 }
    },
    tblHeaderRoot: {
      height: 32,
      "& .MuiTableCell-stickyHeader": {
        background: toolbarBG1
      },

      "& th": {
        fontSize: 12,
        fontWeight: 700,
        color: textGrayH1
      }
    },
    lblEmpty: {
      display: "block",
      margin: "auto",
      fontWeight: 400,
      marginTop: 80,
      color: pprBorderColorA
    }
  })
);

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      borderBottom: `1px solid ${pprBorderColorA}`,
      borderRadius: "12px 12px 0px 0px"
    },
    highlight:
      theme.palette.mode === "light"
        ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85)
        }
        : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark
        },
    title: {
      flex: "1 1 100%",
      fontFamily: "Manrope",
      fontStyle: "normal",
      fontWeight: "bold",
      fontSize: 16,
      color: strongGray
    }
  })
);

type Order = "asc" | "desc";

interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  {
    id: "order_info",
    numeric: true,
    disablePadding: true,
    label: "Order"
  },
  {
    id: "despatches_date",
    numeric: true,
    disablePadding: false,
    label: "Despatch Date"
  },
  { id: "status", numeric: true, disablePadding: false, label: "Status" },
  { id: "sender_name", numeric: false, disablePadding: false, label: "Sender" }
];

function EnhancedTableHead() {
  const cssClasses = useStyles();
  return (
    <TableHead className={cssClasses.tblHeaderRoot}>
      <TableRow>
        {headCells.map(headCell => (
          <TableCell key={headCell.id} align="center" padding="none">
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const EnhancedTableToolbar = () => {
  const classes = useToolbarStyles();

  return (
    <Toolbar className={classes.root} variant="dense">
      <Typography className={classes.title} id="tableTitle" component="div">
        Overdue Despatches
      </Typography>

      {/* <Tooltip title="More options">
        <IconButton aria-label="More options">
          <MoreVertRounded />
        </IconButton>
      </Tooltip> */}
    </Toolbar>
  );
};

/** Default Functional Component */
export default function OverdueDespatches() {
  const classes = useStyles();

  const [overdueDespatchesList, setOverdueList] = useState<DispatchProduct[]>([]);

  useEffect(() => {
    getTopList();
  }, []);

  const getTopList = async () => {
    const overdueDespatches: any = await GetTopOverdueDespatches();
    if (overdueDespatches) {
      setOverdueList(
        overdueDespatches !== undefined ? overdueDespatches.data : undefined
      );
    }
  };

  return (
    <Grid item sm={6} md={4} xs={12}>
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <EnhancedTableToolbar />
          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size="small"
              aria-label="enhanced table"
              stickyHeader
            >
              <EnhancedTableHead />
              <TableBody>
                {overdueDespatchesList.length > 0 &&
                  overdueDespatchesList.map((info, index) => (
                    <TableRow
                      className={`tblrow ${index % 2 == 0 ? "light" : "darker"
                        }`}
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                    >
                      <TableCell
                        id={info?.id?.toString() || index.toString()}
                        scope="row"
                        padding="none"
                        align="center"
                      >
                        {info?.purchaseOrderId}
                      </TableCell>
                      <TableCell scope="row" padding="none" align="center">
                        {formatInTimeZone("dd/MM/yyyy HH:mm a", Intl.DateTimeFormat().resolvedOptions().timeZone, info?.dispatch?.createdAt)}
                      </TableCell>
                      <TableCell align="center">
                        {(info?.dispatch?.status) ? info?.dispatch?.status : "-"}
                      </TableCell>
                      <TableCell align="center">
                        {(info?.dispatch?.dispatchOriginAddresses && info?.dispatch?.dispatchOriginAddresses?.length > 0) ? info?.dispatch?.dispatchOriginAddresses[0]?.name : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            {overdueDespatchesList.length === 0 && (
              <Typography
                className={classes.lblEmpty}
                variant="h4"
                align="center"
              >
                No Data
              </Typography>
            )}
          </TableContainer>
        </Paper>
      </div>
    </Grid>
  );
}
